/** Freemium v2 earned-trial banner (dashboard + practice).
 * - Free user who never had a trial: "Answer 20 questions to unlock 7 days of
 *   Pro, free" with an X/20 progress bar (+ readiness hint if not done).
 * - Active trial: "Your 7-day Pro trial is on, ends <date>" with a /plan/today CTA.
 * Renders nothing unless features.freemium_v2 is ON, so old behaviour is unchanged.
 */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { usePublicSettings } from "@/lib/siteSettings";
import { useAuth } from "@/context/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtDate = (iso) => {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString(undefined, { weekday: "short", day: "numeric", month: "short" });
  } catch { return ""; }
};

const TrialUnlockBanner = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const settings = usePublicSettings();
  const freemiumV2 = settings["features.freemium_v2"] === true;
  const copy = settings["paywall_copy"] || {};
  const [st, setSt] = useState(null);

  useEffect(() => {
    if (!user || user.role === "admin" || !freemiumV2) return;
    let on = true;
    axios.get(`${API}/auth/trial/status`).then((r) => { if (on) setSt(r.data); }).catch(() => {});
    return () => { on = false; };
  }, [user, freemiumV2, user?.subscription_status, user?.earned_trial_notice]);

  if (!freemiumV2 || !st) return null;

  // Active earned trial → "your trial is on, ends <date>"
  if (st.trial_active) {
    const endTxt = (copy.trial_started_banner || "Your 7-day Pro trial is on, ends {date}").replace("{date}", fmtDate(st.trial_ends_at));
    return (
      <div data-testid="trial-active-banner" className="glass rounded-2xl border border-primary/30 bg-primary/5 p-4 flex flex-col sm:flex-row sm:items-center gap-3">
        <div className="w-9 h-9 rounded-xl bg-primary/15 flex items-center justify-center flex-shrink-0">
          <Sparkles className="w-4 h-4 text-primary" />
        </div>
        <p className="flex-1 text-sm font-semibold" data-testid="trial-active-text">{endTxt}</p>
        <Button size="sm" className="rounded-full flex-shrink-0" data-testid="trial-active-cta" onClick={() => navigate("/plan/today")}>
          {copy.trial_started_cta || "See my plan"}
        </Button>
      </div>
    );
  }

  // Free, never had a trial → progress to unlock
  if (st.eligible) {
    const answered = st.answered || 0;
    const needed = st.needed || 20;
    const pct = Math.min(100, Math.round((answered / needed) * 100));
    return (
      <div data-testid="trial-unlock-banner" className="glass rounded-2xl border border-primary/25 bg-primary/5 p-4">
        <div className="flex items-center gap-2 mb-2">
          <Sparkles className="w-4 h-4 text-primary flex-shrink-0" />
          <p className="text-sm font-semibold" data-testid="trial-unlock-title">
            {copy.trial_progress_title || "Answer 20 questions to unlock 7 days of Pro, free"}
            {!st.readiness_done && (
              <span className="font-normal text-muted-foreground"> {copy.trial_progress_readiness_hint || "or finish the readiness check"}</span>
            )}
          </p>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex-1 h-2 rounded-full bg-secondary overflow-hidden">
            <div className="h-full bg-primary transition-all" style={{ width: `${pct}%` }} />
          </div>
          <span className="text-xs font-semibold tabular-nums" data-testid="trial-unlock-progress">{answered}/{needed}</span>
        </div>
      </div>
    );
  }

  return null;
};

export default TrialUnlockBanner;
